<template>
  <Container class="items-center">
    <SubNavigation
      v-if="resolvePageType === 'isPage'"
      :title="data.title"
      :children-links="data?.parentNodePage?.children"
      class="mb-10 w-screen xl:mb-17.5"
    />
    <div
      v-else
      class="mb-10 flex flex-col items-center gap-5 md:mb-15 md:gap-5.5 xl:mb-12.5 2xl:mb-20"
    >
      <h1
        class="w-83.75 text-center font-header text-[26px] leading-[116%] md:w-150.75 md:text-4.5xl lg:w-195 xl:w-281.5 xl:text-6xl 2xl:w-331.5 2xl:text-[70px]/[70px]"
      >
        {{ data.title }}
      </h1>

      <div v-if="categoryNames" class="flex flex-wrap items-center justify-center gap-2.5">
        <TypeBadge
          v-for="category in categoryNames"
          :key="category?.id || category?.name"
          :text="category?.name"
        />
      </div>
    </div>

    <CascadingImages
      :images="data?.featuredImage"
      :background="data?.background"
    />
  </Container>

  <div
    v-if="data.perex"
    :class="[
      `mx-5 mb-20 max-w-396.75 border-b border-gray pb-3.75 md:mb-25 md:pb-6.25 lg:mx-10 xl:mb-17.5 xl:pb-9 2xl:mx-auto 2xl:mb-35 2xl:pb-7.5`,
    ]"
  >
    <p
      class="font-header text-xl leading-[110%] md:text-2.5xl xl:text-4.5xl xl:leading-[105%] 2xl:text-[47px]/[49px]!"
    >
      {{ data?.perex }}
    </p>
  </div>

  <Container
    v-if="characteristics.length > 0"
    class="mx-auto my-10 w-full gap-10 md:my-15 md:w-151 md:gap-15 md:px-0! lg:w-153.75 xl:mt-23.5 xl:mb-17.5 xl:w-185 xl:gap-17.5 2xl:w-215.5!"
  >
    <dl class="flex flex-col">
      <div
        v-for="(item, index) in characteristics"
        :key="index"
        class="border-gray flex flex-col gap-7.5 border-t py-2.5 last:border-b xl:gap-7.5 xl:py-3.75"
      >
        <dt
          class="text-sm/[115%] md:text-sm xl:text-[17px]/[21px] 2xl:text-xl/[24px]!"
        >
          {{ item.title }}
        </dt>
        <dd
          class="m-0 text-[17px]/[21px] md:text-xl/[24px] xl:text-[26px]/[30px] 2xl:text-3xl/[35px]!"
        >
          {{ item.value }}
        </dd>
      </div>
    </dl>
  </Container>

  <div :class="{ 'is-colored-bg': isCustomColoredBg }">
    <DynamicContentResolver
      v-if="data.content_blocks"
      :blocks="data?.content_blocks"
    />
  </div>
</template>

<script setup lang="ts">
import type { Notice, AppEvent, Program, PageData } from '~/types'

const props = defineProps<{ type: string; pageData?: PageData }>()

const { locale, t } = useI18n()
const setI18nParams = useSetI18nParams()

const route = useRoute()
const { throwFetchError, throwNotFound } = useThrowErrors()
const breadcrumbsData = useBreadcrumbs()

const resolvePageType = computed(() => {
  const types: Record<string, string> = {
    announcement: 'isNoticePage',
    event: 'isEventPage',
    program: 'isProgram',
    page: 'isPage',
  }
  return types[props.type] || 'isEventPage'
})

const fetchConfig: Record<string, { query: string; key: string }> = {
  isNoticePage: {
    query: 'GetAnnouncementBySlug',
    key: 'getAnnouncementBySlug',
  },
  isEventPage: { query: 'GetEventBySlug', key: 'getEventBySlug' },
  isProgram: { query: 'GetProgramBySlug', key: 'getProgramBySlug' },
  isPage: { query: 'GetPageBySlug', key: 'getPageBySlug' },
}

const currentConfig = fetchConfig[resolvePageType.value]

// For type=page, pageData is passed from [...slug].vue to avoid a duplicate request
const rawData = ref<any>(null)

if (props.pageData) {
  rawData.value = props.pageData
} else {
  const { data: fetchRes, error: fetchError } = await usePersistedFetch(
    currentConfig?.query as any,
    { slug: route.params.slug?.at(-1) },
  )
  if (fetchError.value) throwFetchError()
  if (!fetchRes.value?.[currentConfig?.key as string]) throwNotFound()
  rawData.value = fetchRes.value
}

const data = computed<Notice | AppEvent | Program | Page>(() => {
  if (!rawData.value) return null
  return rawData.value[currentConfig?.key as string] ?? rawData.value
})

watch(
  data,
  (val) => {
    if (val) {
      setI18nParams({
        cs: { slug: useGetSlugSegments(val, 'cs') },
        en: { slug: useGetSlugSegments(val, 'en') },
      })
    }
  },
  { immediate: true },
)

onMounted(() => {
  breadcrumbsData.value = data.value?.breadcrumbs[locale.value] || []
})

const categoryNames = computed(() => {
  if (resolvePageType.value === 'isEventPage')
    return (data.value as AppEvent).eventTags
  if (resolvePageType.value === 'isNoticePage')
    return (data.value as Notice).announcementTags
  if (resolvePageType.value === 'isProgram')
    return (data.value as Program).programTags
  return null
})

const themeColor = computed(() => {
  return data.value?.background && data.value?.background !== 'white'
    ? `#${data.value?.background}`
    : resolvePageType.value === 'isNoticePage'
      ? '#E4E4E4'
      : '#fff'
})

const isGrayLightBg = computed(() => {
  const bg = themeColor.value.toLowerCase()
  return bg === '#e4e4e4'
})

const isCustomColoredBg = computed(() => {
  const bg = themeColor.value.toLowerCase()
  return bg !== '#fff' && bg !== '#ffffff' && bg !== '#e4e4e4'
})

useDynamicSeo(data, {
  bodyAttrs: {
    style: computed(
      () =>
        `background-color: ${themeColor.value}; transition: background-color 0.5s ease;`,
    ).value,
  },
})

useHead(
  computed(() => ({
    bodyAttrs: {
      class: [
        isGrayLightBg.value ? 'is-gray-light-bg' : '',
        isCustomColoredBg.value ? 'is-colored-page-bg' : '',
      ]
        .filter(Boolean)
        .join(' '),
    },
  })),
)

const formattedDate = useFormattedDate(
  () => (data.value as AppEvent)?.starts_at,
  () => (data.value as AppEvent)?.ends_at,
)

const characteristics = computed(() => {
  const chars = []

  if (formattedDate.value) {
    chars.push({ title: t('common.date'), value: formattedDate.value })
  }
  if ((data.value as any).location) {
    chars.push({
      title: t('common.location'),
      value: (data.value as any).location,
    })
  }

  return chars
})
</script>
